import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';

interface RevenueCompositionRow {
  revenueType: string;
  totalValue: number;
  count: number;
}

interface MarginRow {
  opportunityId: string;
  name: string;
  revenue: number;
  cost: number;
  margin: number;
  marginPercent: number;
  status: string;
}

function formatCurrency(value: number): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `$${value.toFixed(0)}`;
  }
}

const REVENUE_TYPE_LABELS: Record<string, string> = {
  LICENSE: 'License',
  SUBSCRIPTION: 'Subscription',
  SERVICE: 'Service',
  SUPPORT: 'Support',
  RESALE: 'Resale',
};

// Story 76.3 — Meeting Intelligence types
interface OverallMeetingStats {
  totalMeetings: number;
  avgDurationMins: number | null;
  stageAdvanceRate: number;
  nextActionConversionRate: number;
  activeReps: number;
}

interface RepMeetingStats {
  repId: string;
  repEmail: string;
  totalMeetings: number;
  avgDurationMins: number | null;
  stageAdvanceRate: number;
  nextActionConversionRate: number;
}

interface TypeMeetingStats {
  meetingType: string;
  totalMeetings: number;
  avgDurationMins: number | null;
  stageAdvanceRate: number;
  nextActionConversionRate: number;
}

interface MeetingIntelligenceReport {
  overall: OverallMeetingStats;
  perRep: RepMeetingStats[];
  perType: TypeMeetingStats[];
}

export function AnalyticsPage() {
  const { t } = useTranslation();
  const [composition, setComposition] = useState<RevenueCompositionRow[]>([]);
  const [margins, setMargins] = useState<MarginRow[]>([]);
  const [compositionLoading, setCompositionLoading] = useState(true);
  const [marginsLoading, setMarginsLoading] = useState(true);
  const [marginsForbidden, setMarginsForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [meetingReport, setMeetingReport] = useState<MeetingIntelligenceReport | null>(null);

  const loadComposition = useCallback(async () => {
    setCompositionLoading(true);
    try {
      const { data } = await apiClient.get<RevenueCompositionRow[]>(
        '/analytics/revenue-composition',
      );
      setComposition(data);
    } catch {
      setError('Failed to load revenue composition');
    } finally {
      setCompositionLoading(false);
    }
  }, []);

  const loadMargins = useCallback(async () => {
    setMarginsLoading(true);
    setMarginsForbidden(false);
    try {
      const { data } = await apiClient.get<MarginRow[]>('/analytics/margins');
      setMargins(data);
    } catch (err: unknown) {
      const status =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 403) {
        setMarginsForbidden(true);
      } else {
        setError('Failed to load margin data');
      }
    } finally {
      setMarginsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadComposition();
    loadMargins();
    // 76.3 — meeting intelligence (silently ignores 403 for reps)
    apiClient.get<MeetingIntelligenceReport>('/analytics/meeting-intelligence')
      .then(({ data }) => setMeetingReport(data))
      .catch(() => {/* manager-only — reps see nothing */});
  }, [loadComposition, loadMargins]);

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold">Revenue &amp; Margin Analytics</h1>

      {error && (
        <div className="rounded-md border border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Revenue Composition */}
      <section className="space-y-3">
        <h2 className="text-lg font-medium">Revenue Composition</h2>
        {compositionLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : (
          <div className="overflow-hidden rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Revenue Type</th>
                  <th className="px-4 py-3 text-right">Total Value</th>
                  <th className="px-4 py-3 text-right">Count</th>
                </tr>
              </thead>
              <tbody>
                {composition.map((row) => (
                  <tr key={row.revenueType} className="border-t">
                    <td className="px-4 py-3 font-medium">
                      {REVENUE_TYPE_LABELS[row.revenueType] ?? row.revenueType}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {formatCurrency(row.totalValue)}
                    </td>
                    <td className="px-4 py-3 text-right">{row.count}</td>
                  </tr>
                ))}
                {composition.length === 0 && (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-6 text-center text-muted-foreground"
                    >
                      No revenue data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Margin Report */}
      {!marginsForbidden && (
        <section className="space-y-3">
          <h2 className="text-lg font-medium">Margin Report</h2>
          {marginsLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : (
            <div className="overflow-hidden rounded-lg border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Opportunity</th>
                    <th className="px-4 py-3 text-right">Revenue</th>
                    <th className="px-4 py-3 text-right">Cost</th>
                    <th className="px-4 py-3 text-right">Margin</th>
                    <th className="px-4 py-3 text-right">Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {margins.map((row) => (
                    <tr key={row.opportunityId} className="border-t">
                      <td className="px-4 py-3 font-medium">{row.name}</td>
                      <td className="px-4 py-3 text-right">
                        {formatCurrency(row.revenue)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {formatCurrency(row.cost)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {formatCurrency(row.margin)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className={
                            row.marginPercent >= 0
                              ? 'text-emerald-600'
                              : 'text-red-600'
                          }
                        >
                          {row.marginPercent.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                  {margins.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-6 text-center text-muted-foreground"
                      >
                        No margin data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Story 76.3 — Meeting Intelligence (managers only) */}
      {meetingReport && (
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-medium">{t('meetingIntelligence.title')}</h2>
            <p className="text-sm text-muted-foreground">{t('meetingIntelligence.subtitle')}</p>
          </div>

          {/* Overall stats */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[
              { label: t('meetingIntelligence.total_meetings'), value: String(meetingReport.overall.totalMeetings) },
              { label: t('meetingIntelligence.avg_duration'), value: meetingReport.overall.avgDurationMins != null ? meetingReport.overall.avgDurationMins.toFixed(1) : t('meetingIntelligence.na') },
              { label: t('meetingIntelligence.stage_advance_rate'), value: `${meetingReport.overall.stageAdvanceRate}%` },
              { label: t('meetingIntelligence.next_action_rate'), value: `${meetingReport.overall.nextActionConversionRate}%` },
              { label: t('meetingIntelligence.active_reps'), value: String(meetingReport.overall.activeReps) },
            ].map((stat) => (
              <div key={stat.label} className="rounded-lg border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground">{stat.label}</div>
                <div className="mt-1 text-lg font-semibold">{stat.value}</div>
              </div>
            ))}
          </div>

          {/* Per rep */}
          {meetingReport.perRep.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('meetingIntelligence.per_rep_title')}</h3>
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-left">{t('meetingIntelligence.col_rep')}</th>
                      <th className="px-4 py-2 text-right">{t('meetingIntelligence.col_count')}</th>
                      <th className="px-4 py-2 text-right">{t('meetingIntelligence.col_avg_duration')}</th>
                      <th className="px-4 py-2 text-right">{t('meetingIntelligence.col_stage_advance')}</th>
                      <th className="px-4 py-2 text-right">{t('meetingIntelligence.col_next_action')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {meetingReport.perRep.map((r) => (
                      <tr key={r.repId} className="hover:bg-muted/20">
                        <td className="px-4 py-2">{r.repEmail}</td>
                        <td className="px-4 py-2 text-right">{r.totalMeetings}</td>
                        <td className="px-4 py-2 text-right">{r.avgDurationMins != null ? r.avgDurationMins.toFixed(1) : t('meetingIntelligence.na')}</td>
                        <td className="px-4 py-2 text-right">{r.stageAdvanceRate}%</td>
                        <td className="px-4 py-2 text-right">{r.nextActionConversionRate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Per meeting type */}
          {meetingReport.perType.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('meetingIntelligence.per_type_title')}</h3>
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-left">{t('meetingIntelligence.col_type')}</th>
                      <th className="px-4 py-2 text-right">{t('meetingIntelligence.col_count')}</th>
                      <th className="px-4 py-2 text-right">{t('meetingIntelligence.col_avg_duration')}</th>
                      <th className="px-4 py-2 text-right">{t('meetingIntelligence.col_stage_advance')}</th>
                      <th className="px-4 py-2 text-right">{t('meetingIntelligence.col_next_action')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {meetingReport.perType.map((r) => (
                      <tr key={r.meetingType} className="hover:bg-muted/20">
                        <td className="px-4 py-2">{r.meetingType.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-2 text-right">{r.totalMeetings}</td>
                        <td className="px-4 py-2 text-right">{r.avgDurationMins != null ? r.avgDurationMins.toFixed(1) : t('meetingIntelligence.na')}</td>
                        <td className="px-4 py-2 text-right">{r.stageAdvanceRate}%</td>
                        <td className="px-4 py-2 text-right">{r.nextActionConversionRate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {meetingReport.overall.totalMeetings === 0 && (
            <p className="text-sm text-muted-foreground">{t('meetingIntelligence.empty')}</p>
          )}
        </section>
      )}
    </div>
  );
}
